import { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { BackendRoute } from '../../../config/index';
import { useLocale } from '../../../hooks/use-locale.hook';
import { ApiService } from '../../../services/index';
import { SPACE_BACKEND_ROUTE } from '../constants/spaces-query.const';
import { TreeSpaceRef } from '../types/data-room.types';
import { DataRoomViewer, useDataRoomViewer } from './use-recipient-view.hook';

/**
 * A zip is written as it is sent, so the request lasts as long as the bytes do — the default half
 * minute would cut a large room off mid-archive, which would look like a failure and be one.
 */
const SPACE_ARCHIVE_REQUEST_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * A workspace or a data room, or one folder of it, as a single `.zip`.
 *
 * Which route carries it is decided before the request, like the rest of the module: the owner's
 * module routes refuse a recipient, and a recipient's route is scoped to the room they were shared.
 *
 * `pendingArchiveIds` names everything being packed — Home can pack several cards at once — so
 * whatever asked for an archive can wait rather than going quiet for half a minute. The same one
 * asked for again while it is packing is not asked for twice.
 */
export const useDownloadSpaceArchive = () => {
  const viewer = useDataRoomViewer();
  const { messagesLocale } = useLocale();
  const [pendingArchiveIds, setPendingArchiveIds] = useState<ReadonlySet<string>>(new Set());
  // The guard reads this rather than the state, which a click right after another has not seen yet.
  const packing = useRef(new Set<string>());

  const settle = useCallback((id: string, isPending: boolean) => {
    if (isPending) {
      packing.current.add(id);
    } else {
      packing.current.delete(id);
    }

    setPendingArchiveIds(new Set(packing.current));
  }, []);

  const downloadArchive = useCallback(
    async ({ folderId, space }: { folderId?: string; space: TreeSpaceRef }) => {
      const id = folderId ?? space.id;

      if (viewer === DataRoomViewer.UNRESOLVED || packing.current.has(id)) return;

      settle(id, true);

      try {
        await ApiService.downloadFile(
          {
            endpoint:
              viewer === DataRoomViewer.RECIPIENT
                ? BackendRoute.MY_DATA_ROOMS
                : SPACE_BACKEND_ROUTE[space.kind],
            routePath: folderId
              ? [space.id, 'folders', folderId, 'archive']
              : [space.id, 'archive'],
          },
          { timeoutMs: SPACE_ARCHIVE_REQUEST_TIMEOUT_MS },
        );
      } catch {
        toast.error(messagesLocale['ARCHIVE_DOWNLOAD_FAILED']);
      } finally {
        settle(id, false);
      }
    },
    [messagesLocale, settle, viewer],
  );

  return { downloadArchive, pendingArchiveIds };
};
